// A review thread marked reviewed: the reviewer checked it, typically one
// another participant resolved, that what they asked for was done. Private
// to the reviewer, a flag kept with their review marks (flags.ts):
//
//   path  `./thread/<thread ID>`;
//   blob  the thread's state when marked (`threadState`): its comments and
//         whether it is resolved, so a reply or a resolution since shows it
//         as changed since reviewed.
import type { ReviewThread } from '../api/queries';
import { anchorId, type Anchor, type Plan } from './anchors';
import { digest, flag } from './flags';

/** Where thread marks' paths start. */
export const THREAD_PATH = './thread/';

/** The path of thread `id`'s marks. */
const pathOf = (id: string) => THREAD_PATH + id;

type Thread = Pick<ReviewThread, 'id' | 'isResolved' | 'comments'>;

/** The thread's state as a mark keeps it: how many comments, the last one, and whether it is resolved. */
export function threadState(t: Thread): string {
  const last = t.comments.nodes[t.comments.nodes.length - 1]?.id ?? '';
  return digest(`${t.id}\0${t.comments.totalCount}\0${last}\0${t.isResolved ? 'resolved' : 'open'}`);
}

/** The mark for thread `t` as it is now. */
export const threadAnchor = (t: Thread): Anchor => flag(pathOf(t.id), threadState(t));

/** `reviewed`: marked as it is now; `changed`: marked, but a comment or its resolution came since; `none`: not marked. */
export type ThreadReview = 'reviewed' | 'changed' | 'none';

export function threadReview(t: Thread, flags: readonly Anchor[]): ThreadReview {
  const path = pathOf(t.id);
  const mine = flags.filter((a) => a.path === path);
  if (!mine.length) return 'none';
  const now = anchorId(threadAnchor(t));
  return mine.some((a) => anchorId(a) === now) ? 'reviewed' : 'changed';
}

/** The writes that mark `t` reviewed as it is now (dropping its marks of earlier states), or clear its marks. */
export function planThread(t: Thread, flags: readonly Anchor[], reviewed: boolean): Plan {
  const path = pathOf(t.id);
  const at = threadAnchor(t), id = anchorId(at);
  const unset = flags.filter((a) => a.path === path && (!reviewed || anchorId(a) !== id));
  return { unset, set: reviewed ? [at] : [] };
}
